import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Group as GroupIcon,
  GridOn as GridIcon,
  SwapHoriz as SwapIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { SupportLayout, InfoCard, VisualExample } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

// Custom mini grid for demonstrating cell groupings
interface CellModeGridProps {
  mode: 'standard' | 'paired-vertical' | 'paired-horizontal' | 'quad';
  showNumbers?: boolean;
  highlightCell?: number;
}

const CellModeGrid: React.FC<CellModeGridProps> = ({ mode, showNumbers = true, highlightCell }) => {
  const getCellConfig = () => {
    switch (mode) {
      case 'paired-vertical':
        return { rows: 5, cols: 10, rowSpan: 2, colSpan: 1, totalCells: 50 };
      case 'paired-horizontal':
        return { rows: 10, cols: 5, rowSpan: 1, colSpan: 2, totalCells: 50 };
      case 'quad':
        return { rows: 5, cols: 5, rowSpan: 2, colSpan: 2, totalCells: 25 };
      default:
        return { rows: 10, cols: 10, rowSpan: 1, colSpan: 1, totalCells: 100 };
    }
  };

  const config = getCellConfig();
  const cellWidth = mode === 'standard' ? 24 : 36;
  const cellHeight = mode === 'standard' ? 24 : 36;

  // For demo, only show first 5 rows/cols
  const displayRows = Math.min(config.rows, 5);
  const displayCols = Math.min(config.cols, 5);

  return (
    <Box sx={{ display: 'inline-block' }}>
      {/* Header row with numbers */}
      {showNumbers && (
        <Box sx={{ display: 'flex', ml: 4 }}>
          {Array.from({ length: displayCols }, (_, j) => (
            <Box
              key={j}
              sx={{
                width: cellWidth * config.colSpan + (config.colSpan - 1) * 1,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.light',
                color: 'white',
                fontSize: 10,
                fontWeight: 600,
                borderRight: 1,
                borderColor: 'primary.main',
              }}
            >
              {config.colSpan > 1 ? `${j * config.colSpan} ${j * config.colSpan + 1}` : j}
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex' }}>
        {/* Side numbers */}
        {showNumbers && (
          <Box>
            {Array.from({ length: displayRows }, (_, i) => (
              <Box
                key={i}
                sx={{
                  width: 28,
                  height: cellHeight * config.rowSpan + (config.rowSpan - 1) * 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'error.light',
                  color: 'white',
                  fontSize: 10,
                  fontWeight: 600,
                  borderBottom: 1,
                  borderColor: 'error.main',
                  flexDirection: 'column',
                }}
              >
                {config.rowSpan > 1 ? (
                  <>
                    <span>{i * config.rowSpan}</span>
                    <span>{i * config.rowSpan + 1}</span>
                  </>
                ) : (
                  i
                )}
              </Box>
            ))}
          </Box>
        )}
        {/* Grid cells */}
        <Box>
          {Array.from({ length: displayRows }, (_, i) => (
            <Box key={i} sx={{ display: 'flex' }}>
              {Array.from({ length: displayCols }, (_, j) => {
                const cellNumber = i * (mode === 'paired-horizontal' ? 5 : mode === 'quad' ? 5 : 10) + j + 1;
                const isHighlighted = highlightCell === cellNumber;
                return (
                  <Box
                    key={j}
                    sx={{
                      width: cellWidth * config.colSpan + (config.colSpan - 1) * 1,
                      height: cellHeight * config.rowSpan + (config.rowSpan - 1) * 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isHighlighted ? 'success.main' : 'grey.100',
                      border: 2,
                      borderColor: isHighlighted ? 'success.dark' : 'grey.400',
                      borderRadius: 0.5,
                      fontSize: mode === 'standard' ? 8 : 11,
                      fontWeight: 600,
                      color: isHighlighted ? 'white' : 'text.secondary',
                      m: 0.125,
                    }}
                  >
                    {cellNumber}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
      {displayRows < config.rows && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
          (showing first {displayRows} rows)
        </Typography>
      )}
    </Box>
  );
};

export const ParticipationModesPage: React.FC = () => {
  useDocumentTitle('Participation Modes - Support');

  return (
    <SupportLayout
      title="Participation Modes"
      description="Learn about the different ways to configure your board's square groupings to accommodate different group sizes."
      breadcrumbs={[{ label: 'Participation Modes' }]}
    >
      {/* Overview Section */}
      <Box id="overview" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          What Are Participation Modes?
        </Typography>
        <Typography variant="body1" paragraph>
          Traditional squares pools require exactly 100 participants (one per square). But what if you only have 50 friends who want to play? Or just 25?
          <strong> Participation Modes</strong> solve this by grouping multiple squares into single "cells" that one person owns.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Key concept:</strong> The grid always has 100 squares for scoring purposes, but ownership is grouped into larger cells.
            When any square in your cell wins, you win!
          </Typography>
        </Alert>

        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Mode</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Participants</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Squares per Cell</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cell Shape</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Best For</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Chip label="Standard" size="small" color="default" />
                </TableCell>
                <TableCell>100</TableCell>
                <TableCell>1</TableCell>
                <TableCell>1x1</TableCell>
                <TableCell>Large groups, traditional pools</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="Paired (Vertical)" size="small" color="primary" />
                </TableCell>
                <TableCell>50</TableCell>
                <TableCell>2</TableCell>
                <TableCell>2x1 (tall)</TableCell>
                <TableCell>Medium groups</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="Paired (Horizontal)" size="small" color="primary" />
                </TableCell>
                <TableCell>50</TableCell>
                <TableCell>2</TableCell>
                <TableCell>1x2 (wide)</TableCell>
                <TableCell>Medium groups</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="Quad" size="small" color="secondary" />
                </TableCell>
                <TableCell>25</TableCell>
                <TableCell>4</TableCell>
                <TableCell>2x2</TableCell>
                <TableCell>Small groups, office pools</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Standard Mode */}
      <Box id="standard-mode" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <GridIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Standard Mode (100 Squares)
        </Typography>

        <Typography variant="body1" paragraph>
          The classic squares pool format. Each of the 100 squares is owned by a single participant.
          This is the default mode and works best for large groups.
        </Typography>

        <Grid container spacing={3} alignItems="stretch">
          <Grid size={{ xs: 12, md: 6 }}>
            <VisualExample title="Standard 10x10 Grid" caption="Each square is independently owned">
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CellModeGrid mode="standard" />
              </Box>
            </VisualExample>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Characteristics</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="100 unique owners" secondary="One person per square" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Traditional experience" secondary="Familiar to most players" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Maximum variety" secondary="100 different number combinations" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Paired Modes */}
      <Box id="paired-modes" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <SwapIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Paired Modes (50 Cells)
        </Typography>

        <Typography variant="body1" paragraph>
          Paired modes group two adjacent squares into one cell. You can choose vertical pairs (2 rows, 1 column)
          or horizontal pairs (1 row, 2 columns). Each participant owns 2 squares, giving them twice the chance to win!
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <VisualExample title="Vertical Pairs (2x1)" caption="Squares stacked vertically - each cell spans 2 rows">
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CellModeGrid mode="paired-vertical" highlightCell={1} />
              </Box>
            </VisualExample>
            <Paper elevation={0} sx={{ p: 2, mt: 2, border: 1, borderColor: 'info.light', bgcolor: 'info.50' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Vertical Pair Layout</Typography>
              <Typography variant="body2">
                Cell 1 owns squares at rows 0 and 1, column 0. The header shows both row numbers (0 and 1) stacked vertically.
                This creates a 5-row by 10-column visual grid.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <VisualExample title="Horizontal Pairs (1x2)" caption="Squares side by side - each cell spans 2 columns">
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CellModeGrid mode="paired-horizontal" highlightCell={1} />
              </Box>
            </VisualExample>
            <Paper elevation={0} sx={{ p: 2, mt: 2, border: 1, borderColor: 'info.light', bgcolor: 'info.50' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Horizontal Pair Layout</Typography>
              <Typography variant="body2">
                Cell 1 owns squares at row 0, columns 0 and 1. The header shows both column numbers (0 and 1) side by side.
                This creates a 10-row by 5-column visual grid.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Double the chances!</strong> In paired mode, you win if EITHER square in your cell matches the winning score.
            For example, if you own cells with numbers (0,3) and (1,3), you win on scores ending in 0-3 OR 1-3.
          </Typography>
        </Alert>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Neighbor Payouts:</strong> Neighbor payouts (both regular and box) are not available in Paired mode because neighbor relationships must align with individual square ownership.
          </Typography>
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Quad Mode */}
      <Box id="quad-mode" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <GroupIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Quad Mode (25 Cells)
        </Typography>

        <Typography variant="body1" paragraph>
          Quad mode groups four squares (2x2) into one cell, perfect for smaller groups of 25 participants.
          Each owner has four chances to win per quarter!
        </Typography>

        <Grid container spacing={3} alignItems="stretch">
          <Grid size={{ xs: 12, md: 6 }}>
            <VisualExample title="Quad Layout (2x2)" caption="Each cell contains 4 squares in a 2x2 arrangement">
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CellModeGrid mode="quad" highlightCell={1} />
              </Box>
            </VisualExample>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>How Quad Cells Work</Typography>
              <Typography variant="body2" paragraph>
                Cell 1 owns all four squares in the top-left 2x2 block:
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" fontFamily="monospace">
                  (Row 0, Col 0) - (Row 0, Col 1)<br />
                  (Row 1, Col 0) - (Row 1, Col 1)
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                If the randomized numbers give your cell corners like (7,2), (7,3), (8,2), and (8,3),
                you win on any score ending in:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="7-2" size="small" />
                <Chip label="7-3" size="small" />
                <Chip label="8-2" size="small" />
                <Chip label="8-3" size="small" />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <InfoCard title="Great for Small Groups" variant="tip" icon={<GroupIcon />}>
            <Typography variant="body2">
              Quad mode is perfect for office Super Bowl parties, small friend groups, or family gatherings
              where you don't have 100 people but still want everyone to participate.
            </Typography>
          </InfoCard>
        </Box>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Neighbor Payouts:</strong> Neighbor payouts (both regular and box) are not available in Quad mode because neighbor relationships must align with individual square ownership.
          </Typography>
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Setting Up */}
      <Box id="setup" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <SettingsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Setting Up Participation Mode
        </Typography>

        <Typography variant="body1" paragraph>
          You can set the participation mode when creating a board or editing an existing board (before publishing).
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>When Creating a Board</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Click 'Create Board'" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Find 'Participation Mode' in settings" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Select your preferred mode" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Complete other settings and save" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>Using Templates</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Templates save participation mode" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Create templates for different group sizes" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Edit template to change default mode" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Participation mode can only be changed before the board is published.
            Once players start joining, the mode is locked to ensure fairness.
          </Typography>
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Visual Display */}
      <Box id="visual-display" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          How It Looks on the Board
        </Typography>

        <Typography variant="body1" paragraph>
          When you enable a non-standard participation mode, the board visually reflects the cell groupings:
        </Typography>

        <List>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText
              primary="Merged cells"
              secondary="Cells appear as larger rectangles combining their constituent squares"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText
              primary="Header numbers"
              secondary="Row/column headers show multiple numbers stacked or side-by-side for merged cells"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText
              primary="Cell numbers"
              secondary="Each cell displays its number (1-50 or 1-25) instead of 1-100"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText
              primary="Larger icons"
              secondary="Player icons and text scale up to fit the larger cell size"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
            <ListItemText
              primary="Progress tracking"
              secondary="Board shows 'X of 50 cells claimed' or 'X of 25 cells claimed'"
            />
          </ListItem>
        </List>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Winning */}
      <Box id="winning" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          How Winning Works
        </Typography>

        <Typography variant="body1" paragraph>
          Winners are determined the same way as standard mode - by matching the last digit of each team's score.
          The difference is that multiple squares in your cell can match!
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'primary.main', bgcolor: 'primary.50' }}>
          <Typography variant="h6" gutterBottom>Example: Quad Mode Win</Typography>
          <Typography variant="body2" paragraph>
            You own Cell 13, which contains squares at positions:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Chip label="(4,4) = 7-2" variant="outlined" />
            <Chip label="(4,5) = 7-3" variant="outlined" />
            <Chip label="(5,4) = 8-2" variant="outlined" />
            <Chip label="(5,5) = 8-3" variant="outlined" />
          </Box>
          <Typography variant="body2" paragraph>
            If the score at the end of Q1 is <strong>Chiefs 17, Eagles 23</strong>:
          </Typography>
          <Typography variant="body2">
            The winning square is at (7, 3) - that's <strong>your cell</strong>! You win the Q1 payout.
          </Typography>
        </Paper>

        <InfoCard title="Same Payouts, More Chances" variant="tip">
          <Typography variant="body2">
            The payout structure doesn't change based on participation mode. If Q1 pays $100 to the winner,
            you get $100 whether you own 1 square or 4 squares in the winning cell. You just have more
            number combinations that can win!
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* FAQ */}
      <Box id="faq" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Frequently Asked Questions
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Can I have both standard and paired squares on the same board?
          </Typography>
          <Typography variant="body2">
            No, a board can only have one participation mode. All cells on the board use the same grouping.
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            What if someone wants to buy multiple cells?
          </Typography>
          <Typography variant="body2">
            Players can own multiple cells, just like they could own multiple squares in standard mode.
            Use the square request feature or manually assign additional cells as needed.
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Does the price change for larger cells?
          </Typography>
          <Typography variant="body2">
            The "price per square" setting still applies, but you'll typically want to adjust it.
            For example, if standard mode is $10/square ($1000 pot), paired mode might be $20/cell ($1000 pot with 50 cells).
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Which paired mode should I choose - vertical or horizontal?
          </Typography>
          <Typography variant="body2">
            It's mostly aesthetic preference! Vertical pairs (2x1) create a wider grid with taller cells.
            Horizontal pairs (1x2) create a taller grid with wider cells. The odds are identical.
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Can I change the mode after the board is published?
          </Typography>
          <Typography variant="body2">
            No. Once a board is published and players can join, the participation mode is locked.
            This ensures everyone knows what they're signing up for and maintains fairness.
          </Typography>
        </Paper>
      </Box>
    </SupportLayout>
  );
};

export default ParticipationModesPage;
